import { useRequiredContext } from "@redotech/react-util/context";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  AssigneesFilterType,
  ConversationTagFilterType,
  ConversationTagsFilter,
  FilterGroupFilterOption,
  FiltersStatus,
} from "@redotech/redo-model/conversation-filters/conversation-filters";
import { SystemView } from "@redotech/redo-model/conversation-filters/system-view";
import { SPAM_TAG } from "@redotech/redo-model/conversation-tags/conversation-tag-record";
import { SortDirection } from "@redotech/redo-model/tables/table";
import { View } from "@redotech/redo-model/view";
import { deepIntersectionEquals } from "@redotech/util/equal";
import {
  createContext,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { Location, useLocation } from "react-router-dom";
import { ViewsContext } from "../views";

const spamFilter: ConversationTagsFilter = {
  type: FilterGroupFilterOption.CONVERSATION_TAGS,
  value: [SPAM_TAG],
  query: ConversationTagFilterType.ANY_OF,
};

const StaticSystemViews: Record<
  Exclude<SystemView, SystemView.ASSIGNED | SystemView.MENTIONS>,
  View
> = {
  [SystemView.ALL]: {
    _id: "all",
    team: "",
    name: SystemView.ALL,
    filters: { status: FiltersStatus.OPEN, advancedFilters: [] },
  },
  [SystemView.SENT]: {
    _id: "sent",
    team: "",
    name: SystemView.SENT,
    filters: {
      status: FiltersStatus.OPEN,
      sort: { key: "lastMerchantResponse", direction: SortDirection.DESC },
      advancedFilters: [],
    },
  },
  [SystemView.SPAM]: {
    _id: "spam",
    team: "",
    name: SystemView.SPAM,
    filters: { status: FiltersStatus.OPEN, advancedFilters: [spamFilter] },
  },
  [SystemView.DRAFTS]: {
    _id: "drafts",
    team: "",
    name: SystemView.DRAFTS,
    filters: { drafts: true, advancedFilters: [] },
  },
  [SystemView.CREATE]: {
    _id: "create",
    team: "",
    name: SystemView.CREATE,
    filters: { advancedFilters: [] },
  },
};

export const ActiveViewContext = createContext<View>(
  StaticSystemViews[SystemView.ALL],
);

export const SetEditingViewContext = createContext<
  Dispatch<SetStateAction<boolean>>
>(() => {});
export const EditingViewContext = createContext<boolean>(false);

export const ActiveViewContextProvider = memo(
  function ActiveViewContextProvider({
    children,
  }: {
    children: ReactNode | ReactNode[];
  }) {
    const user = useRequiredContext(UserContext);
    const views = useRequiredContext(ViewsContext);
    const location: Location = useLocation();

    const [editingView, setEditingView] = useState<boolean>(false);

    const assignedView: View = {
      _id: "assigned",
      team: "",
      name: SystemView.ASSIGNED,
      filters: {
        advancedFilters: [
          {
            type: FilterGroupFilterOption.ASSIGNEES,
            value: [user._id],
            query: AssigneesFilterType.INCLUDES,
          },
        ],
        status: FiltersStatus.OPEN,
      },
    };

    const mentionsView: View = {
      _id: "mentions",
      team: "",
      name: SystemView.MENTIONS,
      filters: {
        advancedFilters: [
          { type: FilterGroupFilterOption.MENTIONS, value: [user._id] },
        ],
        status: FiltersStatus.OPEN,
      },
    };

    const [currentView, setCurrentView] = useState<View>(
      getViewFromLocation(location, [assignedView, mentionsView, ...views]),
    );

    useEffect(() => {
      if (currentView._id === SystemView.CREATE) {
        setEditingView(true);
      }
    }, [currentView]);

    useEffect(() => {
      const newViewToSet = getViewFromLocation(location, [
        assignedView,
        mentionsView,
        ...views,
      ]);

      if (deepIntersectionEquals(newViewToSet, currentView)) {
        return;
      }

      setCurrentView(newViewToSet);
      // FIXME
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, views, currentView]);

    return (
      <EditingViewContext.Provider value={editingView}>
        <SetEditingViewContext.Provider value={setEditingView}>
          <ActiveViewContext.Provider value={currentView}>
            {children}
          </ActiveViewContext.Provider>
        </SetEditingViewContext.Provider>
      </EditingViewContext.Provider>
    );
  },
);

function getViewFromLocation(location: Location, viewOptions: View[]): View {
  const viewNameMatch = location.pathname.match(/\/support\/table\/(.*)/);
  const rawViewName = viewNameMatch ? viewNameMatch[1] : "";
  const decodedViewName = decodeURIComponent(rawViewName);
  const view = [...viewOptions, ...Object.values(StaticSystemViews)].find(
    (view) => view.name === decodedViewName,
  );

  if (!view) {
    console.log("Could not determine view from url. Using default view");
  }

  const newViewToSet = view || StaticSystemViews[SystemView.ALL];

  if (!newViewToSet.filters) {
    newViewToSet.filters = { advancedFilters: [] };
  }

  /** If it's a system default view, load the sort direction from local storage */
  if (
    newViewToSet.name &&
    (Object.values(SystemView) as string[]).includes(newViewToSet.name)
  ) {
    const sortDirection = localStorage.getItem(
      `redo.support.lastResponse-sort-direction-view-${newViewToSet.name}`,
    );
    if (sortDirection) {
      newViewToSet.filters.sort = {
        key: "lastResponse",
        direction: sortDirection as SortDirection,
      };
    }
  }

  if (!newViewToSet.filters.sort) {
    newViewToSet.filters.sort = {
      key: "lastResponse",
      direction: SortDirection.DESC,
    };
  }

  return { ...newViewToSet };
}
